// CB:BEGIN admin-design-tokens
/**
 * CoreBids Admin Design Tokens
 *
 * Single source of truth for admin UI colors, typography sizes, and spacing.
 * Use these constants instead of hardcoding values in component files.
 */

// ─── Typography ───────────────────────────────────────────────────────────────

export const adminType = {
  pageTitle: { fontSize: 22, fontWeight: 700, color: '#111827' } as const,
  sectionTitle: { fontSize: 15, fontWeight: 600, color: '#111827' } as const,
  label: { fontSize: 12, fontWeight: 500, color: '#6b7280' } as const,
  metricLarge: { fontSize: 28, fontWeight: 700, color: '#111827', lineHeight: 1 } as const,
  metricMedium: { fontSize: 20, fontWeight: 700, color: '#111827', lineHeight: 1 } as const,
  body: { fontSize: 14, color: '#374151' } as const,
  caption: { fontSize: 12, color: '#6b7280' } as const,
};

// ─── Surfaces ─────────────────────────────────────────────────────────────────

export const adminSurface = {
  page: '#f9fafb',
  card: '#ffffff',
  subtle: '#f3f4f6',
  hover: '#f9fafb',
};

// ─── Borders ──────────────────────────────────────────────────────────────────

export const adminBorder = {
  default: '1px solid #e5e7eb',
  strong: '1px solid #d1d5db',
  focus: '1px solid #3b82f6',
  radius: { sm: 6, md: 8, lg: 12 },
};

// ─── Status pills ─────────────────────────────────────────────────────────────

export const statusPill: Record<string, { bg: string; text: string; dot: string; label: string }> = {
  // Project statuses
  DRAFT:        { bg: '#f3f4f6', text: '#374151', dot: '#9ca3af',  label: 'Draft' },
  REQUESTED:    { bg: '#eff6ff', text: '#1e40af', dot: '#3b82f6',  label: 'Requested' },
  POSTED:       { bg: '#eff6ff', text: '#1e40af', dot: '#3b82f6',  label: 'Posted' },
  BIDDING:      { bg: '#fefce8', text: '#854d0e', dot: '#eab308',  label: 'Bidding' },
  BIDS_COLLECTING: { bg: '#fefce8', text: '#854d0e', dot: '#eab308', label: 'Collecting Bids' },
  BIDS_READY:   { bg: '#f0fdf4', text: '#166534', dot: '#22c55e',  label: 'Bids Ready' },
  DECIDING:     { bg: '#faf5ff', text: '#6b21a8', dot: '#a855f7',  label: 'Deciding' },
  HOMEOWNER_CONTRACT_SIGNING: { bg: '#eff6ff', text: '#1e40af', dot: '#60a5fa', label: 'Signing' },
  ESCROW_PENDING: { bg: '#fff7ed', text: '#9a3412', dot: '#f97316', label: 'Escrow Pending' },
  ACTIVE_P1:    { bg: '#f0fdf4', text: '#14532d', dot: '#16a34a',  label: 'Active – P1' },
  ACTIVE_P2:    { bg: '#f0fdf4', text: '#14532d', dot: '#16a34a',  label: 'Active – P2' },
  ACTIVE_P3:    { bg: '#f0fdf4', text: '#14532d', dot: '#16a34a',  label: 'Active – P3' },
  IN_PROGRESS:  { bg: '#f0fdf4', text: '#14532d', dot: '#16a34a',  label: 'In Progress' },
  BID_SELECTED: { bg: '#eff6ff', text: '#1e3a8a', dot: '#2563eb',  label: 'Bid Selected' },
  SCHEDULED:    { bg: '#eff6ff', text: '#1e3a8a', dot: '#2563eb',  label: 'Scheduled' },
  COMPLETED:    { bg: '#f3f4f6', text: '#374151', dot: '#6b7280',  label: 'Completed' },
  CLOSED:       { bg: '#f3f4f6', text: '#374151', dot: '#6b7280',  label: 'Closed' },
  CANCELLED:    { bg: '#fef2f2', text: '#991b1b', dot: '#ef4444',  label: 'Cancelled' },
  DISPUTED:     { bg: '#fef2f2', text: '#991b1b', dot: '#ef4444',  label: 'Disputed' },
  OUT_OF_SCOPE: { bg: '#fef2f2', text: '#991b1b', dot: '#6b7280',  label: 'Out of Scope' },
};

export function getStatusPill(status: string): { bg: string; text: string; dot: string; label: string } {
  return statusPill[status] ?? { bg: '#f3f4f6', text: '#374151', dot: '#9ca3af', label: status };
}

// ─── Shadows ──────────────────────────────────────────────────────────────────

export const adminShadow = {
  card: '0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05)',
  elevated: '0 4px 12px rgba(0,0,0,0.08)',
  focus: '0 0 0 3px rgba(59, 130, 246, 0.25)',
};

// ─── Common button styles ─────────────────────────────────────────────────────

export const adminBtn = {
  primary: {
    background: '#1d4ed8',
    color: '#ffffff',
    border: 'none',
    borderRadius: 6,
    padding: '7px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  } as React.CSSProperties,

  secondary: {
    background: '#ffffff',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    padding: '7px 14px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  } as React.CSSProperties,

  danger: {
    background: '#dc2626',
    color: '#ffffff',
    border: 'none',
    borderRadius: 6,
    padding: '7px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  } as React.CSSProperties,

  ghost: {
    background: 'transparent',
    color: '#374151',
    border: 'none',
    padding: '6px 10px',
    fontSize: 13,
    cursor: 'pointer',
  } as React.CSSProperties,
};

// ─── Card styles ──────────────────────────────────────────────────────────────

export const adminCard: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  padding: '18px 20px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};

// ─── Icon box helper ──────────────────────────────────────────────────────────

export const iconBoxStyle = (gradient: string): React.CSSProperties => ({
  width: 40,
  height: 40,
  flexShrink: 0,
  background: gradient,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

// Need React type for CSSProperties
import type React from 'react';
// CB:END admin-design-tokens
